import { useEffect, useState } from 'react';
import SingleToolHeader from '@quicktools/shared-components/components/common/SingleToolHeader/SingleToolHeader';
import LayoutForTextApps from '@quicktools/shared-components/components/common/LayoutForTextApps/LayoutForTextApps';
import NexHead from '@quicktools/shared-components/components/common/NextHead';
import { useTranslation } from 'react-i18next';
import { SectionWithStickyInput, WithBreadcrumb } from '@quicktools/shared-components';
import { isMobileOnly } from 'react-device-detect';
import SkinToneDropDown from '@quicktools/shared-components/components/domainSpecific/text/SkinToneDropdown';
import { IBreadcrumb } from '@quicktools/shared-components/components/core/Breadcrumb/types';
import getBreadcrumbSublinks from '@quicktools/shared-components/utils/breadcrumbSubLinks';
import useSnackbarStore from '@quicktools/shared-components/hooks/snackbar';
import SnackbarList from '@quicktools/shared-components/components/core/SnackbarList';
import { jsonLdScriptProps } from 'react-schemaorg';
import { WebSite } from 'schema-dts';

import FilterModal from '../FilterModal';
import Emojis from '../Emojis';
import DeviceFilter from '../DeviceFilter';
import CategoryFilter from '../CategoryFilter';
import { TOOL_NAME, SEARCH_STR } from '../../configs';
import { mapLastSublink, scrollToSearchInput } from '../../helpers';
import analytics from '../../services/analytics';
import Editor from '../Editor';

import useApp from './useApp';
import { ICategoryPageProps } from './types';
import useStyles from './styles';

export default function CategoryPage({ emojis, children, category }: ICategoryPageProps) {
  const styles = useStyles();
  const { t } = useTranslation();
  const seoTitle = t(`categories.${category}.seoTitle`);
  const seoDescription = t(`categories.${category}.seoDescription`);
  const title = t(`categories.${category}.title`);
  const description = t(`categories.${category}.description`);
  const searchPlaceholder = t(`categories.${category}.search`);
  const canonicalPath = category === 'index' ? 'text/emojis/' : `text/emojis/${category}/`;
  const [breadcrumbItems, setBreadcrumbItems] = useState<IBreadcrumb[]>([]);
  const snackItems = useSnackbarStore((state) => state.snackItems);
  const closeSnack = useSnackbarStore((state) => state.snackbarClose);
  const fullUrl = `https://tools.picsart.com/${canonicalPath}`;
  const [stickyOffset, setStickyOffset] = useState(0);

  const {
    skinTone,
    showModal,
    searchFound,
    currentEmojis,
    onChooseEmoji,
    displaySkintone,
    emojiDeviceType,
    onChangeCategory,
    onChangeSkintone,
    onChangeSearchText,
    onToggleFilterModal,
    debouncedSearchTerm,
    onChangeEmojiDeviceType,
    defaultDeviceType,
  } = useApp(emojis);

  useEffect(() => {
    analytics.pageOpen();
  }, []);

  useEffect(() => {
    const sublinks = getBreadcrumbSublinks(canonicalPath);
    const lastSublink = sublinks[sublinks.length - 1];
    const mappedLastSublink = mapLastSublink(lastSublink);
    sublinks.pop();
    sublinks.push(mappedLastSublink);
    setBreadcrumbItems(sublinks);
  }, [canonicalPath]);

  return (
    <>
      <NexHead title={seoTitle} description={seoDescription} canonicalPath={canonicalPath}>
        <script
          {...jsonLdScriptProps<WebSite>({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            url: fullUrl,
            potentialAction: [
              {
                '@type': 'SearchAction',
                target: {
                  '@type': 'EntryPoint',
                  urlTemplate: `${fullUrl}?${SEARCH_STR}={search_term_string}`,
                },
                'query-input': 'required name=search_term_string',
              },
            ],
          })}
        />
      </NexHead>
      <div className={styles.root}>
        <SnackbarList
          className={styles.notifications}
          snackItems={snackItems}
          onClose={closeSnack}
        />
        <SingleToolHeader analytics={analytics} toolName={TOOL_NAME} />
        <WithBreadcrumb items={breadcrumbItems} />

        <LayoutForTextApps
          stickyOffset={stickyOffset}
          stickyInputChild={
            <SectionWithStickyInput
              onChange={onChangeSearchText}
              onFilterClick={onToggleFilterModal}
              onInputClick={() => {
                analytics.textBoxClick();
                isMobileOnly && scrollToSearchInput();
              }}
              primaryText={title}
              placeholder={searchPlaceholder}
              secondaryText={description}
              onStickyOffsetChange={setStickyOffset}
              defaultValue={debouncedSearchTerm}
              hasSearchIcon
              autoFocus
            />
          }
          sidebarChild={
            <>
              <CategoryFilter
                heading="Select category"
                onSelectItem={onChangeCategory}
                selectedCategory={category}
              />
              <DeviceFilter
                heading="Select device type"
                changeType={onChangeEmojiDeviceType}
                selected={emojiDeviceType}
              />
            </>
          }
          listingBarChild={
            <>
              {debouncedSearchTerm && (
                <div className={styles.resultsMsg}>
                  Search results for{' '}
                  <span className="color--primary">
                    “{debouncedSearchTerm}” ({searchFound})
                  </span>
                </div>
              )}

              <div></div>

              {displaySkintone && (
                <SkinToneDropDown
                  values={['🤚', '🤚🏻', '🤚🏼', '🤚🏽', '🤚🏾', '🤚🏿']}
                  selectedValue={skinTone}
                  setSelectedValue={onChangeSkintone}
                />
              )}
            </>
          }
          listingChild={
            <Emojis
              defaultDeviceType={defaultDeviceType}
              emojisList={currentEmojis}
              type={emojiDeviceType}
              chooseEmoji={onChooseEmoji}
              displayCategory={debouncedSearchTerm.length === 0}
              stickyOffset={stickyOffset}
            />
          }
        >
          {children}
        </LayoutForTextApps>

        <Editor />

        {showModal && (
          <FilterModal
            emojiDeviceType={emojiDeviceType}
            changeCategory={onChangeCategory}
            changeDeviceType={onChangeEmojiDeviceType}
            closeModal={onToggleFilterModal}
            selectedCategory={category}
          />
        )}
      </div>
    </>
  );
}
